import type { NavItem, NavLink } from '@/types'

/**
 * Every page in the sitemap. Link to these instead of typing paths by hand,
 * so a page can be renamed in one place.
 */
export const routes = {
    home: '/',
    about: '/about',
    headteachersWelcome: '/headteachers-welcome',
    visionAndValues: '/vision-and-values',
    curriculum: '/curriculum',
    departments: '/departments',
    staffDirectory: '/staff-directory',
    admissions: '/admissions',
    termDates: '/term-dates',
    calendar: '/calendar',
    news: '/news',
    parents: '/parents',
    students: '/students',
    uniform: '/uniform',
    schoolMeals: '/school-meals',
    clubs: '/clubs-and-activities',
    transport: '/transport',
    send: '/send',
    safeguarding: '/safeguarding',
    policies: '/policies',
    ofsted: '/ofsted',
    performance: '/performance',
    examResults: '/exam-results',
    vacancies: '/vacancies',
    contact: '/contact'
} as const

export const newsArticlePath = (slug: string): string => `${routes.news}/${slug}`

export const externalLinks = {
    ofstedReports: 'https://reports.ofsted.gov.uk/',
    ofstedParentView: 'https://parentview.ofsted.gov.uk/',
    performanceTables: 'https://www.compare-school-performance.service.gov.uk/',
    keyStage4Statistics: 'https://explore-education-statistics.service.gov.uk/find-statistics/key-stage-4-performance/2025-26',
    ceop: 'https://www.ceop.police.uk/Safety-Centre/'
} as const

export const mainNavigation: NavItem[] = [
    {
        label: "About Us",
        description: "Who we are, what we believe and the people who make Elmsworth what it is.",
        links: [
            { label: "About the School", href: routes.about, description: "Our story, our community and our trust" },
            { label: "Headteacher's Welcome", href: routes.headteachersWelcome, description: "A message from Mrs Hale" },
            { label: "School Vision & Values", href: routes.visionAndValues, description: "Ambition, kindness and courage" },
            { label: "Staff Directory", href: routes.staffDirectory, description: "Leadership, pastoral and subject teams" },
            { label: "Vacancies", href: routes.vacancies, description: "Teaching and support roles" }
        ]
    },
    {
        label: "Learning",
        description: "A broad, ambitious curriculum from Year 7 to GCSE, with plenty to explore beyond lessons.",
        links: [
            { label: "Curriculum", href: routes.curriculum, description: "What students learn in every year" },
            { label: "Departments", href: routes.departments, description: "Our subject teams and courses" },
            { label: "Exam Results", href: routes.examResults, description: "GCSE results and headline figures" },
            { label: "Clubs & Activities", href: routes.clubs, description: "Sport, music, drama, DofE and more" }
        ]
    },
    {
        label: "Admissions",
        href: routes.admissions
    },
    {
        label: "Parents",
        description: "Everything families need day to day, from term dates to the lunch menu.",
        links: [
            { label: "Parent Information", href: routes.parents, description: "Absence, letters, contacts and support" },
            { label: "Term Dates", href: routes.termDates, description: "Holidays and training days" },
            { label: "School Calendar", href: routes.calendar, description: "Events, evenings and key dates" },
            { label: "Uniform", href: routes.uniform, description: "What to wear and where to buy it" },
            { label: "School Meals", href: routes.schoolMeals, description: "Menus, prices and free school meals" },
            { label: "Transport", href: routes.transport, description: "Buses, cycling and travel support" },
            { label: "SEND Information", href: routes.send, description: "Support for special educational needs" }
        ]
    },
    {
        label: "Students",
        description: "Timetables, clubs and the support available to every student.",
        links: [
            { label: "Student Information", href: routes.students, description: "Homework, careers and wellbeing" },
            { label: "Clubs & Activities", href: routes.clubs, description: "Over 60 clubs every week" },
            { label: "Term Dates", href: routes.termDates, description: "Holidays and training days" },
            { label: "School Calendar", href: routes.calendar, description: "What is coming up" }
        ]
    },
    {
        label: "Key Information",
        description: "The statutory information every school publishes, gathered in one place.",
        links: [
            { label: "Ofsted Information", href: routes.ofsted, description: "Our latest report card" },
            { label: "Performance Information", href: routes.performance, description: "Headline measures and tables" },
            { label: "Exam Results", href: routes.examResults, description: "GCSE outcomes year by year" },
            { label: "SEND Information", href: routes.send, description: "Our SEND information report" },
            { label: "Safeguarding", href: routes.safeguarding, description: "Keeping every child safe" },
            { label: "Policies & Documents", href: routes.policies, description: "Every policy as a PDF" }
        ]
    },
    {
        label: "News & Events",
        href: routes.news
    }
]

export const quickLinks: NavLink[] = [
    { label: "Term Dates", href: routes.termDates },
    { label: "School Calendar", href: routes.calendar },
    { label: "Uniform", href: routes.uniform },
    { label: "School Meals", href: routes.schoolMeals },
    { label: "Transport", href: routes.transport },
    { label: "Clubs & Activities", href: routes.clubs },
    { label: "Policies", href: routes.policies },
    { label: "Vacancies", href: routes.vacancies }
]

/**
 * The statutory and important information parents look for. Policies live on
 * the Policies & Documents page, each under its own anchor.
 */
export const keyInformationLinks: NavLink[] = [
    { label: "Ofsted reports", href: routes.ofsted, description: "Our latest inspection report card" },
    { label: "Performance information", href: routes.performance, description: "Headline measures and performance tables" },
    { label: "Exam results", href: routes.examResults, description: "GCSE results for recent years" },
    { label: "Pupil premium", href: `${routes.policies}#pupil-premium`, description: "Our strategy statement and its impact" },
    { label: "SEND information report", href: routes.send, description: "How we support every learner" },
    { label: "Safeguarding", href: routes.safeguarding, description: "Our safeguarding team and approach" },
    { label: "Equality information", href: `${routes.policies}#equality`, description: "Our equality objectives" },
    { label: "Accessibility", href: `${routes.policies}#accessibility`, description: "Accessibility plan and statement" },
    { label: "Complaints procedure", href: `${routes.policies}#complaints`, description: "How to raise a concern" },
    { label: "Behaviour policy", href: `${routes.policies}#behaviour`, description: "Our expectations and approach" },
    { label: "Attendance policy", href: `${routes.policies}#attendance`, description: "Reporting absence and our targets" },
    { label: "Admissions policy", href: `${routes.admissions}#admissions-policy`, description: "Our oversubscription criteria" },
    { label: "Child protection policy", href: `${routes.safeguarding}#child-protection`, description: "Procedures to protect children" },
    { label: "Data protection and privacy", href: `${routes.policies}#data-protection`, description: "Privacy notices and GDPR" }
]

export const footerQuickLinks: NavLink[] = [
    { label: "Term Dates", href: routes.termDates },
    { label: "School Calendar", href: routes.calendar },
    { label: "News & Events", href: routes.news },
    { label: "Uniform", href: routes.uniform },
    { label: "School Meals", href: routes.schoolMeals },
    { label: "Transport", href: routes.transport },
    { label: "Vacancies", href: routes.vacancies },
    { label: "Contact Us", href: routes.contact }
]

export const footerKeyLinks: NavLink[] = [
    { label: "Admissions", href: routes.admissions },
    { label: "Ofsted Information", href: routes.ofsted },
    { label: "Performance Information", href: routes.performance },
    { label: "Exam Results", href: routes.examResults },
    { label: "SEND Information", href: routes.send },
    { label: "Safeguarding", href: routes.safeguarding },
    { label: "Policies & Documents", href: routes.policies }
]

export const legalLinks: NavLink[] = [
    { label: "Privacy notice", href: `${routes.policies}#data-protection` },
    { label: "Accessibility", href: `${routes.policies}#accessibility` },
    { label: "Complaints", href: `${routes.policies}#complaints` }
]
